import { observer } from 'mobx-react';
import { stringAction, stringFuncs } from '@monorepo/tools/src/lib/utils/string';
import { FilterModel, IFilterComponentProps, IFilterProto } from '@monorepo/controlled/src/models/filter.model';
import { snakeCase } from 'change-case';
import { FilterStringMenu } from '@monorepo/base/src/components/filters/filter-string-menu/filter-string-menu';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';

interface IStringFilterProto {
	cellKey: string;
	columnName: string;
	entity?: string;
}

interface IStringFilterComponentProps extends IFilterComponentProps {
	cellKey: string;
	columnName: string;
	entity?: string;
}

const StringsFilter = (props: IStringFilterComponentProps) => {
	const { filter, onCancel, onApply, addFilter, editFilter, columnName, cellKey, entity } = props;
	const protoObj = StringFilterPrototype({ columnName, cellKey, entity });

	if (typeof filter?.value === 'number') {
		return <div></div>;
	}

	const onAddStringFilter = (stringFilterProps: { _value: string | string[]; _action?: stringAction }) => {
		const { _value, _action } = stringFilterProps;
		const filterLabel = _action
			? `${columnName} ${_action} ${_value}`
			: `${columnName} is ${typeof _value === 'string' ? _value : _value.join(', ')}`;

		const stringsFilter = new FilterModel({
			label: filterLabel,
			action: _action,
			value: _value,
			prototype: protoObj,
		});

		if (filter?.index && editFilter) {
			editFilter(filter.index, stringsFilter);
		} else {
			if (protoObj?.multi && addFilter) {
				addFilter(stringsFilter);
			}
		}
		onApply();
	};

	return (
		<FilterStringMenu
			title={columnName}
			defaultValue={typeof filter?.value === 'string' ? filter?.value : ''}
			defaultAction={filter?.action}
			onApply={(_action: stringAction, _value: string) => onAddStringFilter({ _action, _value })}
			onCancel={onCancel}
			debugProps={{ dataAttrs: [new DataAttribute('id', `${snakeCase(columnName)}_menu`)] }}
		/>
	);
};

export const StringFilterPrototype = (props: IStringFilterProto): IFilterProto => {
	const { columnName, cellKey, entity } = props;

	return {
		MenuComponent: columnName,
		FilterComponent: observer((props: IFilterComponentProps) => StringsFilter({ ...props, columnName, cellKey, entity })),
		prop: `${entity}${cellKey}`,
		deletable: true,
		multi: true,
		filterFunc<T>(models: Array<any>, filter: FilterModel): Array<T> {
			const { value, action } = filter;

			return models.filter(model => {
				if (!action) {
					return true;
				}
				const actionFunc = stringFuncs[action as stringAction];
				if (!actionFunc) {
					return true;
				}
				if (Array.isArray(model[cellKey])) {
					return model[cellKey].some((val: string) => actionFunc(val || '', typeof value === 'string' ? value : ''));
				}

				return actionFunc(model[cellKey] || '', typeof value === 'string' ? value : '');
			});
		},
	};
};
